import { PaginatedResponse } from "@medusajs/types"
import { FilterFormType } from "modules/vendors/list/components/filter-form"
import { EStatus, UploadItem } from "types/common"
import { queryKeysFactory } from "utils/api/client/query-key-factory"

export type VendorItem = {
  handle: string
  name: string
  brn: number | string
  email: string
  moq: string
  contact_number: string
  address: string | null
  website: string | null
  featured_vendor: boolean
  managed_vendor: string | null
  managing_vendor: string | null
  gst_registered: boolean
  subdomain_name: string
  short_description: string
  profile_logo: string
  invoice_logo: string
  term_and_conditions: string
  setting?: {
    borderWidth: string
    borderStyle: string
    borderColor: string
    boxShadow: string
  }
  settings: { id: string } | null
  registration_date: string
  application_date: string
  position: number
  background_main: string
  id: string
  vendor_reward_point_amount: number | null
  vendor_reward_point_type: string | null
  enable_earning_reward_point: boolean
  enable_spending_reward_point: boolean
  point_exchange_rate: number | null
  point_redemption_cap: number | null
  point_redemption_increment: number | null
  point_expiry_time: string | null
  status: EVendorStatus
  vendor_type?: EVendorType
  admins?: VendorAdminItem[]

  vendor?: {
    name: string
    id: string
  }
  vendor_id?: string
  first_name?: string
  last_name?: string
  role?: string
  api_access?: string
  email_address?: string
  parent_vendor_id?: string
  parent_vendor?: {
    name?: string
    id?: string
  }
}

export type VendorAdminItem = {
  id?: string
  first_name?: string
  last_name?: string
  email_address?: string
  contact_number?: string
  role?: EVendorAdminRole
  status?: EVendorStatus
  registration_date?: string
  api_access?: string
  vendor_id?: string
  created_at?: string
  updated_at?: string
  deleted_at?: string
}

export type VendorListResponse = PaginatedResponse<{
  vendors: VendorItem[]
}>

export type VendorDetailResponse = {
  vendor: VendorItem
}

export type VendorQueryParams = FilterFormType & {
  q?: string
  order?: string
  limit?: number
  offset?: number
  vendor_type?: EVendorType
  vendorId?: string | null
}

export const vendorQueryKeys = queryKeysFactory<"vendors", VendorQueryParams>(
  "vendors"
)

export type AdminVendorCreateForm = {
  status: EVendorStatus
  name: string
  brn: string
  email: string
  contact_number?: string
  featured_vendor: EStatus
  address?: string | null
  gst_registered?: EStatus
  subdomain_name?: string | null
  vendor_type?: EVendorType
  brand_store?: EStatus
  parent_vendor_id?: any | null
  short_description?: string
  term_and_conditions?: string
  registration_date?: string
  website?: string | null

  profile_logo?: UploadItem[]
  invoice_logo?: UploadItem[]

  // admin
  first_name?: string | null
  last_name?: string | null
  admin_email?: string | null
  admin_contact_number?: string | null
  // user_group?: any | null
  password?: string | null
  confirm_password?: string | null
  admin_registration_date?: string | null
}

export type AdminVendorCreateBody = Omit<
  AdminVendorCreateForm,
  "profile_logo" | "invoice_logo" | "featured_vendor" | "gst_registered"
> & {
  profile_logo?: string | null
  invoice_logo?: string | null
  featured_vendor?: boolean
  gst_registered?: boolean
  handle?: string | null
  website?: string | null
  managing_vendor?: string | null
  background_main?: string | null
  application_date?: string | null
  vendor_id?: string | null
  vendorAdmin?: {
    id?: string
    first_name?: string
    last_name?: string
    email_address?: string
    contact_number?: string
    api_access?: string
    password?: string
  }
}

/**
 * Admin PUT /admin/vendors/:id (edit mode).
 * Vendor `name` and `email` are top-level; admin `password` is sent under `vendorAdmin` when provided.
 */
export type AdminVendorUpdateBody = Partial<
  Omit<AdminVendorCreateBody, "vendorAdmin">
> & {
  name?: string
  email?: string
  vendorAdmin?: {
    id?: string
    first_name?: string
    last_name?: string
    email_address?: string
    contact_number?: string
    password?: string
  }
}

export type VendorCreateForm = {
  status: EVendorStatus
  email: string
  contact_number?: string
  vendor_id?: any | null
  registration_date?: string | null | Date
  first_name?: string | null
  last_name?: string | null
  password?: string | null
  confirm_password?: string | null
  email_address?: string | null
}

export type VendorCreateBody = Omit<
  VendorCreateForm,
  | "profile_logo"
  | "invoice_logo"
  | "featured_vendor"
  | "gst_registered"
  | "email_address"
  | "email"
> & {
  profile_logo?: string | null
  invoice_logo?: string | null
  featured_vendor?: boolean
  gst_registered?: boolean
  email_address?: string | null
}

/**
 * Vendor endpoint POST /vendor/:id/vendor-admin
 * Only allowed fields per strict schema (vendor_id derived from path, role forced server-side)
 */
export type VendorAdminVendorEndpointCreateBody = {
  first_name: string
  last_name: string
  email_address: string
  contact_number: string
  password: string
  status?: EVendorStatus
  registration_date?: string
}

/**
 * Vendor endpoint PUT /vendor/:id/vendor-admin/:adminId
 * Allowed fields per strict schema (no password, registration_date, role, api_access).
 * `vendor_id` is accepted to support reassigning an admin to one of the caller's
 * brand stores; the server validates ownership of the target vendor.
 */
export type VendorAdminVendorEndpointUpdateBody = {
  first_name?: string
  last_name?: string
  email_address?: string
  contact_number?: string
  status?: EVendorStatus
  vendor_id?: string
}

export enum EVendorStatus {
  Active = "active",
  Disabled = "disable",
  Suspended = "suspended",
  Pending = "pending",
}

export enum EVendorType {
  BrandStore = "brand_store",
  Vendor = "vendor",
}

export enum EVendorAdminRole {
  VendorSuperAdmin = "vendor_super_admin",
}
export const VendorStatusLabels = {
  [EVendorStatus.Active]: "Active",
  [EVendorStatus.Disabled]: "Disabled",
  [EVendorStatus.Suspended]: "Suspended",
  [EVendorStatus.Pending]: "Pending",
}

export const VendorTypeLabels = {
  [EVendorType.BrandStore]: "Brand store",
  [EVendorType.Vendor]: "Vendor",
}

export const defaultAdminVendorInit: AdminVendorCreateForm = {
  status: EVendorStatus.Active,
  name: "",
  brn: "",
  email: "",
  contact_number: "",
  featured_vendor: EStatus.No,
  address: "",
  gst_registered: EStatus.No,
  subdomain_name: "",
  brand_store: EStatus.No,
  parent_vendor_id: null,
  short_description: "",
  term_and_conditions: "",
  website: "",

  profile_logo: [],
  invoice_logo: [],

  // admin
  first_name: "",
  last_name: "",
  admin_email: "",
  admin_contact_number: "",
  // user_group: null,
  password: "",
  confirm_password: "",
  admin_registration_date: "",
}

export const defaultVendorInit: VendorCreateForm = {
  status: EVendorStatus.Active,
  email: "",
  contact_number: "",
  vendor_id: "",
  first_name: "",
  last_name: "",
  password: "",
  confirm_password: "",
  registration_date: null,
}
